import { Button, buttonVariants, useToast } from '@arabat/ui';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { downloadInvoicePdf, printInvoice } from './invoice-document.js';
import { customerCallUrl, customerChatUrl, useInvoiceShare } from './invoice-actions.js';

/**
 * Print, PDF and — for the store — send to the customer (WhatsApp chat with the link, or the PDF
 * through the share sheet) and call the customer (ADR-0138 amendment 7). Used by the public
 * invoice page and the console's order dialog.
 */
export function InvoiceActions({
  html,
  number,
  storeName,
  customerName,
  customerPhone,
  invoiceUrl,
  forStore,
}: {
  /** The A4 document (fetched or built when first needed). */
  html: () => Promise<string>;
  number: string;
  storeName: string;
  customerName: string;
  customerPhone: string | null;
  /** The address the customer may open (without the store's actions). */
  invoiceUrl: string;
  forStore: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const fileName = `invoice-${number}.pdf`;
  const message = t('invoice.message', {
    name: customerName,
    store: storeName,
    number,
    url: invoiceUrl,
  });
  const doc = useMutation({
    mutationFn: async (kind: 'print' | 'pdf') => {
      const document = await html();
      if (kind === 'print') await printInvoice(document);
      else await downloadInvoicePdf(document, fileName);
    },
    onError: () => toast.error(t('invoice.failed')),
  });
  const pdf = useInvoiceShare(html, fileName, message);
  const share = useMutation({
    mutationFn: pdf.share,
    onSuccess: (outcome) => {
      if (outcome === 'downloaded') toast.success(t('invoice.downloaded'));
      if (outcome === 'tap-again') toast.success(t('invoice.tapAgain'));
    },
    onError: () => toast.error(t('invoice.failed')),
  });
  const chat = customerChatUrl(customerPhone, message);
  const call = customerCallUrl(customerPhone);
  const busy = doc.isPending || share.isPending;
  return (
    <div className="flex flex-wrap gap-2" data-testid="invoice-actions">
      <Button
        size="sm"
        variant="secondary"
        loading={doc.isPending && doc.variables === 'print'}
        disabled={busy}
        onClick={() => doc.mutate('print')}
      >
        {t('invoice.print')}
      </Button>
      <Button
        size="sm"
        variant="secondary"
        loading={doc.isPending && doc.variables === 'pdf'}
        disabled={busy}
        onClick={() => doc.mutate('pdf')}
      >
        {t('invoice.pdf')}
      </Button>
      {forStore && chat ? (
        <a
          className={buttonVariants({ size: 'sm', variant: 'primary' })}
          href={chat}
          target="_blank"
          rel="noreferrer"
        >
          {t('invoice.send')}
        </a>
      ) : null}
      {forStore ? (
        <Button
          size="sm"
          variant="secondary"
          loading={share.isPending}
          disabled={busy}
          onClick={() => share.mutate()}
        >
          {pdf.ready ? t('invoice.shareReady') : t('invoice.sharePdf')}
        </Button>
      ) : null}
      {forStore && call ? (
        <a className={buttonVariants({ size: 'sm', variant: 'secondary' })} href={call}>
          {t('invoice.call')}
        </a>
      ) : null}
    </div>
  );
}
